import {
  useStripe,
  useElements,
  PaymentElement,
} from "@stripe/react-stripe-js";
import { useState, useEffect } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "../../context/authContext";
import { useToast } from "../../context/toastContext";
import { extractErrorMessage } from "../../utils";
import useAuthedApi from "../../hooks/useAuthedApi";
import PromoCodeInput from "../subscription/PromoCodeInput";
import { baseAxios } from "../../apis";

const useSubscribeMutation = ({
  onClose,
  setOpenDialog,
  setLoadingSubscribe,
  fetchSubscription,
  user,
  promoCode, // Add this parameter
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { authedPost } = useAuthedApi();
  const { addToast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ customerId, priceId, paymentMethodId }) => {
      const payload = {
        customerId,
        priceId,
        paymentMethodId,
      };

      // Only add promoCode if it's not empty
      if (promoCode && promoCode.trim()) {
        payload.promotionCode = promoCode.trim();
      }

      const res = await authedPost(
        "/api/subscribe-with-payment-method/",
        payload
      );

      const { clientSecret, subscriptionId } = res.data;

      console.log("res.data", res.data);

      if (!clientSecret) {
        return { subscriptionId };
      }

      const { error: confirmError, paymentIntent } =
        await stripe.confirmCardPayment(clientSecret);

      if (confirmError || paymentIntent.status !== "succeeded") {
        throw new Error(
          confirmError?.message || "Payment confirmation failed."
        );
      }

      return { subscriptionId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["subscriptionData"] });
      fetchSubscription(user?.stripe_customer_id);
      setOpenDialog("success-subscribe");
      onClose();
    },
    onError: (err) => {
      addToast("error", extractErrorMessage(err), 10000);
    },
    onSettled: () => {
      setLoadingSubscribe(false);
    },
  });
};

const CheckoutForm = ({
  onClose,
  customerId,
  priceId,
  setLoadingSubscribe,
  setOpenDialog,
  plan,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentElementLoaded, setPaymentElementLoaded] = useState(false);
  const { fetchSubscription, user } = useAuth();
  const { authedPost } = useAuthedApi();
  const [setupClientSecret, setSetupClientSecret] = useState(null);
  const { addToast } = useToast(); // Make sure you have access to this
  const [promoCode, setPromoCode] = useState("");

  // state untuk hasil promo
  const [promoChecking, setPromoChecking] = useState(false);
  const [promoError, setPromoError] = useState(null);
  const [promoResult, setPromoResult] = useState(null);

  useEffect(() => {
    const fetchSetupIntent = async () => {
      const res = await authedPost("/api/create-setup-intent/", {
        customerId,
      });
      setSetupClientSecret(res.data.clientSecret);
    };
    if (customerId) fetchSetupIntent();
  }, [customerId]);

  const subscribeMutation = useSubscribeMutation({
    onClose,
    setOpenDialog,
    setLoadingSubscribe,
    fetchSubscription,
    user,
    promoCode,
  });

  // apply / cek promo ke backend
  const handleApplyPromo = async () => {
    const code = promoCode.trim();
    if (!code || !customerId) {
      setPromoResult(null);
      setPromoError(null);
      return;
    }

    try {
      setPromoChecking(true);
      setPromoError(null);

      const res = await baseAxios.get(
        `/api/subscriptions/check-promo/${customerId}/?product=${priceId}&promo_code=${encodeURIComponent(
          code
        )}`
      );

      // res.data = { code, discountPercent, discountAmount, couponName, duration, ... }
      setPromoResult(res.data);
    } catch (err) {
      setPromoError(extractErrorMessage(err?.response?.data?.error || err));
      setPromoResult(null);
    } finally {
      setPromoChecking(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoadingSubscribe(true);

    if (!stripe || !elements) {
      // Can't proceed, handle this early.
      setLoadingSubscribe(false);
      return;
    }

    const { setupIntent, error } = await stripe.confirmSetup({
      elements,
      confirmParams: { return_url: window.location.href },
      redirect: "if_required",
    });

    if (error) {
      addToast("error", extractErrorMessage(error), 10000);
      setLoadingSubscribe(false);
      return;
    }

    const paymentMethodId = setupIntent?.payment_method;
    if (!paymentMethodId) {
      addToast(
        "error",
        "Could not retrieve payment method. Please try again.",
        10000
      );
      setLoadingSubscribe(false);
      return;
    }

    // If we get here, everything is good to go. Call the mutation.
    subscribeMutation.mutate({ customerId, priceId, paymentMethodId });
  };

  useEffect(() => {
    if (!promoCode.trim()) {
      setPromoResult(null);
      setPromoError(null);
      return;
    }

    const timeout = setTimeout(handleApplyPromo, 800); // waits 0.6s after typing stops

    return () => clearTimeout(timeout);
  }, [promoCode, customerId]);

  //  coun summary harga
  // base price dari Stripe plan (cent → dollars)
  const baseAmount = plan?.unit_amount ? plan.unit_amount / 100 : 0;

  // default tanpa promo
  let discountAmount = 0;
  let discountPercent = 0;
  let finalAmount = baseAmount;

  if (promoResult) {
    // ambil percent kalau ada
    discountPercent = promoResult.discountPercent || 0;

    if (promoResult.discountAmount != null) {
      // kalau backend kirim nominal diskon langsung
      discountAmount = promoResult.discountAmount;
    } else if (discountPercent > 0 && baseAmount > 0) {
      // hitung dari percent
      discountAmount = (baseAmount * discountPercent) / 100;
    }

    finalAmount = Math.max(baseAmount - discountAmount, 0);
  }

  return (
    <form onSubmit={handleSubmit}>
      {!paymentElementLoaded ? (
        <div className="border rounded-lg p-4 w-full max-w-md space-y-4 animate-pulse bg-white">
          <div className="h-5 w-24 bg-gray-300 rounded" />
          <div className="h-10 bg-gray-200 rounded w-full" />
          <div className="flex gap-2">
            <div className="h-10 bg-gray-200 rounded w-1/2" />
            <div className="h-10 bg-gray-200 rounded w-1/2" />
          </div>
          <div className="h-10 bg-gray-200 rounded w-full" />
          <div className="space-y-2 pt-2">
            <div className="h-3 w-3/4 bg-gray-200 rounded" />
            <div className="h-3 w-2/3 bg-gray-200 rounded" />
          </div>
        </div>
      ) : null}

      <div className={paymentElementLoaded ? "block" : "hidden"}>
        {/* Add PromoCodeInput component here */}
        <PromoCodeInput
          value={promoCode}
          onChange={setPromoCode}
          onClear={() => {
            setPromoCode("");
            setPromoResult(null);
            setPromoError(null);
          }}
          onApply={handleApplyPromo}
          loading={promoChecking}
        />

        {promoError && (
          <p className="mt-[-.45rem] mb-[.25rem] text-[10px] text-red-500">
            {promoError}
          </p>
        )}

        {/* payment summary */}
        <div className="mb-3 mt-2 p-3 rounded-lg border text-xs">
          <div className="flex justify-between">
            <span className="text-gray-600">Plan name</span>
            <span className="font-medium">{plan?.product_name || "-"}</span>
          </div>
          <div className="flex justify-between mt-2">
            <span className="text-gray-600">Plan price</span>
            <span className="font-medium">${baseAmount.toFixed(2)}</span>
          </div>

          {promoCode && promoResult && (
            <div className="flex justify-between mt-2">
              <span className="text-gray-600">
                Promo <span className="font-semibold">{promoCode}</span>
              </span>
              <span className="font-medium text-green-600">
                -${discountAmount.toFixed(2)}
              </span>
            </div>
          )}

          <div className="flex justify-between mt-2 border-t pt-2">
            <span className="font-semibold text-gray-800">Total</span>
            <span className="font-bold text-gray-900">
              ${finalAmount.toFixed(2)}
            </span>
          </div>
        </div>

        <PaymentElement
          onReady={() => setPaymentElementLoaded(true)}
          // options={{
          //   fields: {
          //     billingDetails: {
          //       name: "never",
          //       email: "never",
          //       phone: "never",
          //       address: "never",
          //     },
          //   },
          // }}
        />
      </div>
      <button
        type="submit"
        disabled={
          !stripe || subscribeMutation.isPending || !paymentElementLoaded
        }
        className="w-full relative overflow-hidden !py-3 mt-4 !font-bold !text-lg !bg-[#007A78]"
      >
        {subscribeMutation.isPending ? "Processing..." : "Confirm Subscription"}
      </button>
    </form>
  );
};

export default CheckoutForm;
